import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, authFetch } from "../api/client.ts";
import { CostEstimate } from "./CostEstimate.tsx";
import { formatUsageWithCost, useCostDisplay } from "../lib/cost.ts";
import { Bot, Check, ChevronLeft, ChevronRight, ImageIcon, Loader2, Maximize2, X } from "lucide-react";

export interface ImageFindingView {
  fieldKey: string;
  value: unknown;
  observation: string;
}

export interface GalleryImage {
  id: string;
  pageNumber: number;
  indexOnPage: number;
  width: number;
  height: number;
  analysis: {
    id: string;
    findings: ImageFindingView[];
    summary: string | null;
    modelId: string;
    inputTokens: number;
    outputTokens: number;
    createdAt: string;
  } | null;
}

/**
 * Images are served behind authentication, so an <img src> cannot fetch them
 * directly; this loads them through authFetch and shows a blob URL.
 */
function AuthImage({ imageId, variant, alt, className }: { imageId: string; variant: "thumb" | "full"; alt: string; className?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    setUrl(null);
    setFailed(false);
    authFetch(`/api/extraction/images/${imageId}/${variant}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        objectUrl = URL.createObjectURL(await res.blob());
        if (!revoked) setUrl(objectUrl);
      })
      .catch(() => { if (!revoked) setFailed(true); });
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [imageId, variant]);

  if (failed) return <div className={`flex items-center justify-center text-[11px] text-muted-foreground ${className ?? ""}`}>Could not load</div>;
  if (!url) return <div className={`flex items-center justify-center ${className ?? ""}`}><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>;
  return <img src={url} alt={alt} className={className} />;
}

function formatValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(String).join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value && typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * The images embedded in the paper's PDF. Nothing is sent to the AI unless a
 * reviewer asks: open an image and choose "Analyse with AI", or tick several
 * and analyse them together. Findings are suggestions applied one at a time.
 */
export function ImageGallery({
  studyId,
  reviewId,
  fieldLabel,
  currentValue,
  onApply,
  openImageId,
  onOpenImage,
}: {
  studyId: string;
  reviewId: string;
  fieldLabel: (key: string) => string;
  currentValue: (key: string) => unknown;
  onApply: (key: string, value: unknown) => void;
  openImageId: string | null;
  onOpenImage: (id: string | null) => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [analysing, setAnalysing] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const queryKey = ["study-images", studyId, reviewId];
  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => api.get<{ hasPdf: boolean; images: GalleryImage[] }>(`/extraction/${studyId}/images?reviewId=${reviewId}`),
    staleTime: 5 * 60 * 1000,
  });
  const images = data?.images ?? [];

  const analyse = async (imageIds: string[]) => {
    setError(null);
    setNotice(null);
    setAnalysing(new Set(imageIds));
    try {
      const result = await api.post<{ analyses: Array<{ findings: ImageFindingView[] }>; failures: Array<{ error: string }> }>(
        `/extraction/${studyId}/${reviewId}/images/analyse`,
        { imageIds }
      );
      const found = result.analyses.reduce((n, a) => n + a.findings.length, 0);
      if (imageIds.length > 1) {
        setNotice(
          `${result.analyses.length} of ${imageIds.length} images analysed: ${found} suggested ${found === 1 ? "value" : "values"}. ` +
            (found > 0 ? "Open an image to review and apply them." : "")
        );
      }
      if (result.failures.length > 0) setError(result.failures[0]!.error);
      setSelected(new Set());
      await qc.invalidateQueries({ queryKey });
      await qc.invalidateQueries({ queryKey: ["highlights", studyId, reviewId] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Image analysis failed.");
    } finally {
      setAnalysing(new Set());
    }
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 10) next.add(id);
      return next;
    });

  if (isLoading) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
        Pulling the images out of the PDF…
      </div>
    );
  }
  if (isError) return <p className="p-6 text-sm text-destructive">Could not load the images for this paper.</p>;
  if (!data?.hasPdf) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        <ImageIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
        <p className="font-medium text-foreground mb-1">No PDF uploaded</p>
        <p className="text-xs">Upload the paper's PDF in the full-text screening step to see its images here.</p>
      </div>
    );
  }
  if (images.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        <ImageIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
        <p className="font-medium text-foreground mb-1">No images found in this PDF</p>
        <p className="text-xs max-w-sm mx-auto">
          Charts drawn as vector graphics are not stored as images inside a PDF, so they do not appear here.
          Open the PDF itself to see those.
        </p>
      </div>
    );
  }

  const openIndex = images.findIndex((i) => i.id === openImageId);
  const selectedIds = [...selected];

  return (
    <div className="p-4 space-y-3">
      <p className="text-xs text-muted-foreground">
        {images.length} {images.length === 1 ? "image" : "images"} found in the PDF. Nothing is sent to the AI unless you
        ask: open an image to view it larger and analyse it, or tick several to analyse them together.
      </p>

      {selected.size > 0 && (
        <div className="sticky top-0 z-10 flex items-center gap-3 flex-wrap border rounded-md bg-card px-3 py-2 shadow-sm">
          <span className="text-xs font-medium">{selected.size} selected</span>
          <button
            onClick={() => void analyse(selectedIds)}
            disabled={analysing.size > 0}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary rounded-md text-xs hover:bg-primary/5 disabled:opacity-50"
          >
            {analysing.size > 0 ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bot className="h-3.5 w-3.5" />}
            Analyse selected with AI
          </button>
          <CostEstimate path={`/extraction/${studyId}/${reviewId}/images/estimate`} body={{ imageIds: selectedIds }} compact />
          <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-muted-foreground hover:text-foreground">
            Clear selection
          </button>
        </div>
      )}
      {notice && <p className="text-xs text-green-800 bg-green-50 border border-green-200 rounded-md px-3 py-2">{notice}</p>}
      {error && <p className="text-xs text-destructive bg-destructive/5 border border-destructive/30 rounded-md px-3 py-2">{error}</p>}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {images.map((image) => {
          const isSelected = selected.has(image.id);
          return (
            <div
              key={image.id}
              className={`relative group border rounded-md overflow-hidden bg-muted/30 ${isSelected ? "ring-2 ring-primary" : ""}`}
            >
              <button
                type="button"
                onClick={() => onOpenImage(image.id)}
                className="block w-full"
                title="View larger"
              >
                <AuthImage imageId={image.id} variant="thumb" alt={`Image on page ${image.pageNumber}`} className="w-full h-32 object-contain bg-white" />
              </button>
              <label className="absolute top-1.5 left-1.5 flex items-center justify-center h-5 w-5 rounded bg-white/90 border shadow-sm cursor-pointer" title="Select for AI analysis">
                <input type="checkbox" checked={isSelected} onChange={() => toggle(image.id)} className="accent-primary" />
              </label>
              <Maximize2 className="absolute top-1.5 right-1.5 h-4 w-4 text-white drop-shadow opacity-0 group-hover:opacity-100 pointer-events-none" />
              <div className="flex items-center justify-between gap-1 px-2 py-1 text-[11px] border-t bg-card">
                <span className="text-muted-foreground">Page {image.pageNumber}</span>
                {analysing.has(image.id) ? (
                  <Loader2 className="h-3 w-3 animate-spin text-primary" />
                ) : image.analysis ? (
                  <span className="text-primary font-medium">
                    Analysed: {image.analysis.findings.length} {image.analysis.findings.length === 1 ? "value" : "values"}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {openIndex >= 0 && (
        <ImageViewer
          image={images[openIndex]!}
          position={`${openIndex + 1} of ${images.length}`}
          studyId={studyId}
          reviewId={reviewId}
          analysing={analysing.has(images[openIndex]!.id)}
          error={error}
          onAnalyse={() => void analyse([images[openIndex]!.id])}
          onPrev={openIndex > 0 ? () => onOpenImage(images[openIndex - 1]!.id) : null}
          onNext={openIndex < images.length - 1 ? () => onOpenImage(images[openIndex + 1]!.id) : null}
          onClose={() => onOpenImage(null)}
          fieldLabel={fieldLabel}
          currentValue={currentValue}
          onApply={onApply}
        />
      )}
    </div>
  );
}

function ImageViewer({
  image, position, studyId, reviewId, analysing, error, onAnalyse, onPrev, onNext, onClose, fieldLabel, currentValue, onApply,
}: {
  image: GalleryImage;
  position: string;
  studyId: string;
  reviewId: string;
  analysing: boolean;
  error: string | null;
  onAnalyse: () => void;
  onPrev: (() => void) | null;
  onNext: (() => void) | null;
  onClose: () => void;
  fieldLabel: (key: string) => string;
  currentValue: (key: string) => unknown;
  onApply: (key: string, value: unknown) => void;
}) {
  const costDisplay = useCostDisplay();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && onPrev) onPrev();
      if (e.key === "ArrowRight" && onNext) onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  const analysis = image.analysis;
  const usage = analysis ? formatUsageWithCost({ inputTokens: analysis.inputTokens, outputTokens: analysis.outputTokens }, costDisplay) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-card rounded-lg shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col lg:flex-row overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image */}
        <div className="relative flex-1 min-h-0 bg-neutral-100 flex items-center justify-center p-3">
          <AuthImage
            imageId={image.id}
            variant="full"
            alt={`Image on page ${image.pageNumber}`}
            className="max-h-[60vh] lg:max-h-[86vh] max-w-full object-contain bg-white"
          />
          {onPrev && (
            <button onClick={onPrev} className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/90 border shadow hover:bg-white" title="Previous image">
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          {onNext && (
            <button onClick={onNext} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/90 border shadow hover:bg-white" title="Next image">
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Side panel */}
        <div className="w-full lg:w-96 border-t lg:border-t-0 lg:border-l flex flex-col min-h-0">
          <div className="flex items-start justify-between gap-2 px-4 py-3 border-b">
            <div>
              <p className="text-sm font-semibold">Image on page {image.pageNumber}</p>
              <p className="text-[11px] text-muted-foreground">{position}</p>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground" title="Close">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            <div className="space-y-1.5">
              <button
                onClick={onAnalyse}
                disabled={analysing}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary rounded-md text-xs hover:bg-primary/5 disabled:opacity-50"
              >
                {analysing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bot className="h-3.5 w-3.5" />}
                {analysis ? "Analyse again with AI" : "Analyse with AI"}
              </button>
              <CostEstimate path={`/extraction/${studyId}/${reviewId}/images/estimate`} body={{ imageIds: [image.id] }} className="block" />
              <p className="text-[11px] text-muted-foreground">
                The AI reads this image against the review's extraction fields. Its findings are suggestions: apply the ones you agree with.
              </p>
            </div>

            {error && !analysing && <p className="text-xs text-destructive">{error}</p>}

            {analysis && (
              <div className="space-y-2 border-t pt-3">
                <p className="text-xs font-semibold">AI findings</p>
                {analysis.summary && <p className="text-xs text-muted-foreground">{analysis.summary}</p>}
                {analysis.findings.length === 0 ? (
                  <p className="text-xs text-muted-foreground">The AI found nothing in this image for the extraction fields.</p>
                ) : (
                  <ul className="space-y-2">
                    {analysis.findings.map((f) => {
                      const applied = JSON.stringify(currentValue(f.fieldKey)) === JSON.stringify(f.value);
                      return (
                        <li key={f.fieldKey} className="border rounded-md p-2 space-y-1">
                          <p className="text-[11px] font-medium text-muted-foreground">{fieldLabel(f.fieldKey)}</p>
                          <p className="text-xs font-medium">{formatValue(f.value)}</p>
                          {f.observation && <p className="text-[11px] text-muted-foreground italic">{f.observation}</p>}
                          {applied ? (
                            <span className="inline-flex items-center gap-1 text-[11px] text-green-700"><Check className="h-3 w-3" /> In the form</span>
                          ) : (
                            <button
                              onClick={() => onApply(f.fieldKey, f.value)}
                              className="text-[11px] px-2 py-0.5 border rounded hover:bg-accent"
                              title={currentValue(f.fieldKey) != null ? `Replaces the current value: ${formatValue(currentValue(f.fieldKey))}` : undefined}
                            >
                              Use this value
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
                <p className="text-[10px] text-muted-foreground">
                  {analysis.modelId}, {new Date(analysis.createdAt).toLocaleString()}{usage ? `: ${usage}` : ""}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
